import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';

const f = "'IBM Plex Mono', monospace";

type OAuthNamespace = {
  getAuthorizationDetails: (id: string) => Promise<{ data: any; error: any }>;
  approveAuthorization: (id: string) => Promise<{ data: any; error: any }>;
  denyAuthorization: (id: string) => Promise<{ data: any; error: any }>;
};

function oauthApi(): OAuthNamespace {
  return (supabase.auth as unknown as { oauth: OAuthNamespace }).oauth;
}

export default function OAuthConsentPage() {
  const [params] = useSearchParams();
  const authorizationId = params.get('authorization_id') ?? '';
  const [details, setDetails] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    document.title = 'Autorizar acesso — CRIAAA';
  }, []);

  useEffect(() => {
    let active = true;
    (async () => {
      if (!authorizationId) {
        setError('authorization_id ausente');
        return;
      }
      const { data: sess } = await supabase.auth.getSession();
      if (!sess.session) {
        const next = window.location.pathname + window.location.search;
        window.location.href = '/entrar?next=' + encodeURIComponent(next);
        return;
      }
      const { data, error: err } = await oauthApi().getAuthorizationDetails(authorizationId);
      if (!active) return;
      if (err) {
        setError(err.message);
        return;
      }
      const immediate = data?.redirect_url ?? data?.redirect_to;
      if (immediate && !data?.client) {
        window.location.href = immediate;
        return;
      }
      setDetails(data);
    })();
    return () => {
      active = false;
    };
  }, [authorizationId]);

  const decide = async (approve: boolean) => {
    setBusy(true);
    const api = oauthApi();
    const { data, error: err } = approve
      ? await api.approveAuthorization(authorizationId)
      : await api.denyAuthorization(authorizationId);
    if (err) {
      setBusy(false);
      setError(err.message);
      return;
    }
    const target = data?.redirect_url ?? data?.redirect_to;
    if (!target) {
      setBusy(false);
      setError('o servidor de autorização não retornou um redirecionamento.');
      return;
    }
    window.location.href = target;
  };

  const shell: React.CSSProperties = {
    background: '#F7F6F2',
    color: '#0A0A0A',
    minHeight: '100vh',
    fontFamily: f,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
  };

  const card: React.CSSProperties = {
    width: '100%',
    maxWidth: 420,
    border: '0.5px solid rgba(10,10,10,0.4)',
    padding: 24,
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
  };

  const btnBase: React.CSSProperties = {
    fontFamily: f,
    fontSize: 12,
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
    padding: '10px 16px',
    borderRadius: 0,
    cursor: busy ? 'default' : 'pointer',
  };

  if (error) {
    return (
      <main style={shell}>
        <div style={card}>
          <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.18em', opacity: 0.4 }}>
            CRIAAA
          </div>
          <p style={{ fontSize: 14, margin: 0 }}>não foi possível carregar esta autorização: {error}</p>
        </div>
      </main>
    );
  }

  if (!details) {
    return (
      <main style={shell}>
        <div style={card}>
          <p style={{ fontSize: 14, margin: 0, opacity: 0.4 }}>carregando…</p>
        </div>
      </main>
    );
  }

  const clientName = details.client?.name ?? 'um aplicativo';

  return (
    <main style={shell}>
      <div style={card}>
        <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.18em', opacity: 0.4 }}>
          CRIAAA
        </div>
        <h1 style={{ fontSize: 18, fontWeight: 400, margin: 0 }}>Conectar {clientName}</h1>
        <p style={{ fontSize: 14, margin: 0 }}>
          isso permite que {clientName} acesse e edite seu portfólio como você.
        </p>
        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', borderTop: '0.5px solid rgba(10,10,10,0.4)', paddingTop: 20 }}>
          <button
            disabled={busy}
            onClick={() => decide(false)}
            style={{ ...btnBase, background: 'none', border: 'none', opacity: 0.5 }}
          >
            NEGAR
          </button>
          <button
            disabled={busy}
            onClick={() => decide(true)}
            style={{ ...btnBase, background: '#00E87A', border: 'none', color: '#0A0A0A' }}
          >
            APROVAR
          </button>
        </div>
      </div>
    </main>
  );
}
